import type { Key, ReactNode } from "react";
import { noop } from "lodash";
import { AnimatePresence, motion, type PanInfo, type Variants } from "motion/react";

import { Component } from "../../../../commons/types/component";
import cn from "../../libs/class-name";

export type TransitionDirection = "next" | "prev";

const SWIPE_MINIMUM_OFFSET = 75; // px
const SWIPE_MINIMUM_VELOCITY = 300; // px/s, matches the former Hammer.js swipe default (0.3 px/ms)
const SLIDE_OFFSET = 100; // px

const slideVariants: Variants = {
  enter: (direction: TransitionDirection) => ({
    opacity: 0,
    x: direction === "next" ? SLIDE_OFFSET : -SLIDE_OFFSET
  }),
  center: {
    opacity: 1,
    x: 0,
    transition: { delay: 0.25 }
  },
  exit: (direction: TransitionDirection) => ({
    opacity: 0,
    x: direction === "next" ? -SLIDE_OFFSET : SLIDE_OFFSET
  })
};

interface Props extends Component {
  /** Identifies the current slide. Changing it triggers the slide transition. */
  slideKey: Key;
  transitionDirection?: TransitionDirection;
  onNext?: () => void;
  onPrev?: () => void;
  children: ReactNode;
}

const SwipeableContent = ({
  classNames = [],
  slideKey,
  transitionDirection = "next",
  onNext = noop,
  onPrev = noop,
  children
}: Props) => {
  const onPanEnd = (event: PointerEvent, { offset, velocity }: PanInfo): void => {
    if (
      event.type === "pointercancel" ||
      Math.abs(offset.x) < SWIPE_MINIMUM_OFFSET ||
      Math.abs(velocity.x) < SWIPE_MINIMUM_VELOCITY
    ) {
      return;
    }

    if (offset.x < 0) {
      onNext();
    } else {
      onPrev();
    }
  };

  return (
    <motion.div className={cn("SwipeableContent", [], classNames)} onPanEnd={onPanEnd}>
      {/* `custom` is forwarded to exiting slides, so they leave in the current direction and not the one of their last render. */}
      <AnimatePresence custom={transitionDirection}>
        <motion.div
          key={slideKey}
          custom={transitionDirection}
          variants={slideVariants}
          initial="enter"
          animate="center"
          exit="exit"
          transition={{ type: "tween" }}
          className="SwipeableContent__content"
        >
          <div className="SwipeableContent__children">{children}</div>
        </motion.div>
      </AnimatePresence>
    </motion.div>
  );
};

export default SwipeableContent;
